import React, { useContext, useEffect, useReducer, useState } from 'react';
import {
	gripperReducer,
	Gripper,
	GRIPPER_TYPE,
	SENSOR_TYPE,
} from './gripperUtils';
import { ApplicationContext } from '../App';
import { KeyboardContext, KeyboardTrigger } from './Layout';
import { WrappedInput, SideMenu, ComboBox, IORender } from './Settings';
import Button from './PendantButton';
import { COLOR, isWebDeployment } from './shared/shared';
import { Scroll } from './General';

interface GripperSettingsProps {
	slipSheetPresent: boolean;
	handleMadeChanges: (hasChanges: boolean) => void;
	confirmSave: (action: () => Promise<void>) => void;
}
import { Loading } from './Layout';

const Grippers: React.FC<GripperSettingsProps> = ({
	slipSheetPresent,
	handleMadeChanges,
	confirmSave,
}) => {
	const lKeyboardTrigger: KeyboardTrigger = useContext(KeyboardContext);
	const app = useContext(ApplicationContext);

	const [state, dispatch] = useReducer(gripperReducer, {
		grippers: {},
		selectedId: null,
		original: {},
	});

	const selectedGripper = state.selectedId
		? state.grippers[state.selectedId]
		: null;
	const [cantSaveMessage, setCantSaveMessage] = useState('');
	const [madeChanges, setMadeChanges] = useState(false);
	const [markedForDeletion, setMarkedForDeletion] = useState<Set<string>>(
		new Set()
	);

	useEffect(() => {
		const fetchConfig = async () => {
			let rawConfig: Record<string, Gripper>;
			if (isWebDeployment()) {
				const unparsedGripperConfig = localStorage.getItem('gripperConfig');
				rawConfig = unparsedGripperConfig
					? JSON.parse(unparsedGripperConfig)
					: await app.api?.GripperConfig.get();
			} else {
				rawConfig = await app.api?.GripperConfig.get();
			}
			const normalized = Object.entries(rawConfig).reduce((acc, [name, g]) => {
				acc[g.id] = { ...g, name };
				return acc;
			}, {} as Record<string, Gripper>);

			dispatch({ type: 'SET_INITIAL', grippers: normalized });
		};

		fetchConfig();
	}, [app]);

	const updateGripper = (changes: Partial<Gripper>) => {
		if (!state.selectedId) return;
		dispatch({ type: 'UPDATE', id: state.selectedId, changes });
		setMadeChanges(true);
		handleMadeChanges(true);
	};

	const updateGripperName = (newName: string) => {
		if (!state.selectedId) return;

		const nameTaken = Object.values(state.grippers).some(
			g => g.name === newName && g.id !== state.selectedId
		);

		if (nameTaken) return setCantSaveMessage('Gripper name must be unique.');
		if (newName.toLowerCase() === 'default')
			return setCantSaveMessage('Gripper name cannot be "default".');
		if (newName.trim().length === 0)
			return setCantSaveMessage('Gripper name cannot be empty.');

		dispatch({ type: 'RENAME', id: state.selectedId, newName });
		setCantSaveMessage('');
		setMadeChanges(true);
		handleMadeChanges(true);
	};

	const saveChanges = async () => {
		await confirmSave(async () => {
			const out: Record<string, Gripper> = {};
			for (const gripper of Object.values(state.grippers)) {
				out[gripper.name] = gripper;
			}

			// Update main config
			if (isWebDeployment()) {
				localStorage.setItem('gripperConfig', JSON.stringify(out));
			} else {
				await app.api?.GripperConfig.update(out);
			}

			// Handle SKUSpecific reassignment for deleted grippers
			if (markedForDeletion.size > 0) {
				const deletedNames = Array.from(markedForDeletion)
					.map(id => state.original[id]?.name)
					.filter(Boolean);

				const sku_settings = await app.api?.SKUSpecific.get();
				let modified = false;

				if (sku_settings && deletedNames.length > 0) {
					for (const key in sku_settings) {
						const sku = sku_settings[key];
						if ('gripper' in sku && deletedNames.includes(sku.gripper)) {
							sku.gripper = 'Default';
							modified = true;
						}
					}
					if (modified) await app.api?.SKUSpecific.update(sku_settings);
				}
			}

			// Finalize
			dispatch({ type: 'SET_INITIAL', grippers: state.grippers });
			setMarkedForDeletion(new Set());
			setMadeChanges(false);
			handleMadeChanges(false);
		});
	};

	const cancelChanges = () => {
		dispatch({ type: 'CANCEL_CHANGES' });
		setMarkedForDeletion(new Set());
		setMadeChanges(false);
		handleMadeChanges(false);
		setCantSaveMessage('');
	};

	const handleSelectSection = (name: string) => {
		const found = Object.values(state.grippers).find(g => g.name === name);
		if (found) dispatch({ type: 'SELECT', id: found.id });
	};

	const addTcp = () => {
		if (!selectedGripper) return;

		const existingTcpCount = selectedGripper.tcps.length;

		const defaultSensor = {
			id: 1,
			pin: 1,
			ReverseLogic: true,
			sensorType: 'Digital Sensor',
		};

		const defaultActuator = {
			id: 3,
			pin: 0,
			ReverseLogic: false,
		};

		const newSensors = Array(existingTcpCount)
			.fill(null)
			.map(() => ({ ...defaultSensor }));

		const newTcp = {
			offset: { X: 0, Y: 0, Z: 100, Rx: 0, Ry: 0, Rz: 0 },
			sensors: newSensors,
			actuators: [defaultActuator],
		};

		updateGripper({
			tcps: [...selectedGripper.tcps, newTcp],
		});
	};

	const addGripper = () => {
		dispatch({ type: 'ADD' });
		setMadeChanges(true);
		handleMadeChanges(true);
	};

	const deleteGripper = () => {
		if (!state.selectedId) return;
		setMarkedForDeletion(prev => new Set(prev).add(state.selectedId!));
		dispatch({ type: 'DELETE', id: state.selectedId });
		setMadeChanges(true);
		handleMadeChanges(true);
	};

	const deleteTcp = (idx: number) => {
		if (!selectedGripper) return;
		const updated = [...selectedGripper.tcps];
		updated.splice(idx, 1);
		updateGripper({ tcps: updated });
	};

	const updateTcpOffset = (
		value: number,
		tcp: any,
		idx: number,
		axis: keyof typeof tcp.offset
	) => {
		const updated = [...selectedGripper!.tcps];
		updated[idx] = { ...tcp, offset: { ...tcp.offset, [axis]: value } };
		updateGripper({ tcps: updated });
	};

	const addActuator = (tcpIdx: number) => {
		const updated = [...selectedGripper!.tcps];
		updated[tcpIdx].actuators.push({ id: 1, pin: 0, ReverseLogic: false });
		updateGripper({ tcps: updated });
	};

	const deleteActuator = (tcpIdx: number, actIdx: number) => {
		const updated = [...selectedGripper!.tcps];
		updated[tcpIdx].actuators.splice(actIdx, 1);
		updateGripper({ tcps: updated });
	};

	const makeCurrentDefault = () => {
		if (state.selectedId)
			dispatch({ type: 'MAKE_DEFAULT', id: state.selectedId });
		setMadeChanges(true);
		handleMadeChanges(true);
	};

	const setNumericField = <T extends keyof Gripper>(
		field: T,
		val: number | string
	) => {
		if (!selectedGripper) return;
		updateGripper({
			[field]: typeof val === 'string' ? parseFloat(val) : val,
		} as Partial<Gripper>);
	};

	return (
		<div className="full-size flex-col">
			<div className="flex-row full-width flex-between align-center vh-10 padding-bottom border-bottom">
				<div className="flex-row full-height align-center">
					<span className="double-margin-left double-margin-top font-5 bold-font">
						Grippers
					</span>
					<div className="double-padding"></div>
				</div>
				{cantSaveMessage.length > 0 && (
					<div className="flex-center full-height padding-top">
						<span className="font-5 color-red">Gripper name must be unique.</span>
					</div>
				)}

				<div className="flex-row full-height align-center">
					<Button
						className="vw-8 double-margin-right double-margin-top"
						pAction={cancelChanges}
						pNormalColorOverride={COLOR.DARK_BLUE}
					>
						<span className="bold-font">Cancel</span>
					</Button>
					<Button
						className="vw-8 double-margin-right double-margin-top"
						pAction={() => saveChanges()}
						pNormalColorOverride={madeChanges ? COLOR.LIGHT_BLUE : COLOR.DARK_GREY}
						pDisabled={!madeChanges}
					>
						<span className="bold-font">Save</span>
					</Button>
				</div>
			</div>
			<div className="vh-80  full-width grid-col-20">
				<div className="full-size flex-col flex-start vh-91 align-center padding-top margin-left">
					{Object.keys(state.grippers).length > 0 ? (
						<>
							<Button
								className="full-width-minus-pad margin-top margin-bottom"
								pAction={addGripper}
								pNormalColorOverride={COLOR.DARK_BLUE}
							>
								<span className="bold-font">Add Gripper</span>
							</Button>
							<SideMenu
								sections={Object.values(state.grippers).map(g => g.name)}
								selectedSection={selectedGripper?.name ?? ''}
								onSelectSection={handleSelectSection}
								unshaded={true}
								subtitles={Object.values(state.grippers).map(g =>
									g.active ? 'Default' : ''
								)}
							/>
						</>
					) : (
						<Loading />
					)}
				</div>

				{selectedGripper !== null && (
					<Scroll
						className="flex-col width-94 double-margin-left border-left double-padding-left"
						key={selectedGripper?.name}
					>
						<div className="padding"></div>
						<div className="flex-col full-width-minus-pad">
							<div className="flex-row full-width flex-between ">
								<div
									className="flex-col"
									style={{ width: '100%', marginRight: '1vw', maxWidth: '857px' }}
								>
									<span className="font-5 margin-bottom bold-font color-blue">Name</span>
									{/* ...also prevent duplicate names */}
									<WrappedInput
										field={'Gripper Name'}
										value={selectedGripper?.name || ''}
										unitsSystem={void 0}
										setter={pNewValue => updateGripperName(String(pNewValue))}
										triggerKeyboard={lKeyboardTrigger}
									/>
								</div>
								<div
									className="flex-row full-height align-center"
									style={{ alignItems: 'end' }}
								>
									<Button
										className="vw-8 "
										pAction={makeCurrentDefault}
										pDisabled={selectedGripper.active}
										pNormalColorOverride={
											selectedGripper.active ? COLOR.DARK_GREY : COLOR.DARK_BLUE
										}
									>
										<span className="bold-font">Make Default</span>
									</Button>
									<Button
										className="vw-8 margin-left "
										pAction={deleteGripper}
										pNormalColorOverride={
											Object.keys(state.grippers).length > 1
												? COLOR.DARK_BLUE
												: COLOR.DARK_GREY
										}
										pDisabled={Object.keys(state.grippers).length <= 1}
									>
										<span className="bold-font">Delete Gripper</span>
									</Button>
								</div>
							</div>
						</div>
						<div className="flex-col full-width-minus-pad">
							<div className="double-padding"></div>
							<span className="font-5 margin-bottom bold-font color-blue">Type</span>
							<ComboBox
								values={Object.values(GRIPPER_TYPE)}
								currentValue={selectedGripper.type}
								onChange={val => {
									const newGripper = { ...selectedGripper };
									newGripper.type = val as GRIPPER_TYPE;
									updateGripper(newGripper);
								}}
							/>
						</div>
						<div className="flex-col full-width-minus-pad">
							<div className="double-padding"></div>
							<span className="font-5 margin-bottom bold-font color-blue">
								Weight (kg)
							</span>
							<WrappedInput
								field={'Weight'}
								value={selectedGripper.weight}
								unitsSystem={void 0}
								setter={val => setNumericField('weight', val)}
								triggerKeyboard={lKeyboardTrigger}
							/>
						</div>
						<div className="flex-col full-width full-width-minus-pad">
							<div className="double-padding"></div>
							<span className="font-5 margin-bottom bold-font color-blue">
								Center of Gravity
							</span>
							<div className="flex-row">
								<div className="flex-col">
									<span className="margin-bottom bold-font color-blue">X (mm)</span>
									<WrappedInput
										field={'X'}
										value={selectedGripper.cog.x}
										unitsSystem={void 0}
										setter={val => {
											const newGripper = { ...selectedGripper };
											newGripper.cog.x = typeof val === 'string' ? parseFloat(val) : val;
											updateGripper(newGripper);
										}}
										triggerKeyboard={lKeyboardTrigger}
									/>
								</div>
								<div className="flex-col double-margin-left">
									<span className="margin-bottom bold-font color-blue">Y (mm)</span>
									<WrappedInput
										field={'Y'}
										value={selectedGripper.cog.y}
										unitsSystem={void 0}
										setter={val => {
											const newGripper = { ...selectedGripper };
											newGripper.cog.y = typeof val === 'string' ? parseFloat(val) : val;
											updateGripper(newGripper);
										}}
										triggerKeyboard={lKeyboardTrigger}
									/>
								</div>
								<div className="flex-col double-margin-left">
									<span className="margin-bottom bold-font color-blue">Z (mm)</span>
									<WrappedInput
										field={'Z'}
										value={selectedGripper.cog.z}
										unitsSystem={void 0}
										setter={val => {
											const newGripper = { ...selectedGripper };
											newGripper.cog.z = typeof val === 'string' ? parseFloat(val) : val;
											updateGripper(newGripper);
										}}
										triggerKeyboard={lKeyboardTrigger}
									/>
								</div>
							</div>
						</div>
						<div className="flex-col full-width-minus-pad">
							<div className="double-padding"></div>
							<span className="font-5 margin-bottom bold-font color-blue">
								Dimensions
							</span>
							<div className="flex-row">
								<div className="flex-col">
									<span className="margin-bottom bold-font color-blue">Width (mm)</span>
									<WrappedInput
										field={'width'}
										value={selectedGripper.width}
										unitsSystem={void 0}
										setter={val => setNumericField('width', val)}
										triggerKeyboard={lKeyboardTrigger}
									/>
								</div>

								<div className="flex-col double-margin-left">
									<span className="margin-bottom bold-font color-blue">Length (mm)</span>
									<WrappedInput
										field={'length'}
										value={selectedGripper.length}
										unitsSystem={void 0}
										setter={val => setNumericField('length', val)}
										triggerKeyboard={lKeyboardTrigger}
									/>
								</div>
							</div>
						</div>

						<div className="flex-col full-width-minus-pad">
							<div className="double-padding"></div>
							<span className="font-5 margin-bottom bold-font color-blue">
								Grip Wait Time (s)
							</span>
							<WrappedInput
								field={'grip wait time'}
								value={selectedGripper.wait_grip}
								unitsSystem={void 0}
								setter={val => setNumericField('wait_grip', val)}
								triggerKeyboard={lKeyboardTrigger}
							/>
						</div>
						<div className="flex-col full-width-minus-pad">
							<div className="double-padding"></div>
							<span className="font-5 margin-bottom bold-font color-blue">
								Release Wait Time (s)
							</span>
							<WrappedInput
								field={'release wait time'}
								value={selectedGripper!.wait_release}
								unitsSystem={void 0}
								setter={val => setNumericField('wait_release', val)}
								triggerKeyboard={lKeyboardTrigger}
							/>
						</div>
						<div className="flex-col full-width-minus-pad">
							<div className="double-padding"></div>
							<span className="font-5 margin-bottom bold-font color-blue">
								Grip Timeout (s)
							</span>
							<WrappedInput
								field={'grip timeout'}
								value={selectedGripper.timeout}
								unitsSystem={void 0}
								setter={val => setNumericField('timeout', val)}
								triggerKeyboard={lKeyboardTrigger}
							/>
						</div>
						{selectedGripper?.main_valve && (
							<div className="flex-col full-width-minus-pad">
								<div className="double-padding"></div>
								<span className="font-5 margin-bottom bold-font color-blue">
									Main Valve
								</span>
								<IORender
									net={selectedGripper.main_valve.id}
									pin={selectedGripper.main_valve.pin}
									inverse_logic={selectedGripper.main_valve.ReverseLogic}
									enabled={selectedGripper.main_valve.enabled}
									onChange={newValve => {
										const updatedGripper = {
											...selectedGripper,
											main_valve: {
												id: newValve.net,
												pin: newValve.pin,
												ReverseLogic: newValve.ReverseLogic,
												enabled: newValve.enabled,
											},
										};
										updateGripper(updatedGripper);
									}}
								/>
							</div>
						)}

						<div className="flex-col full-width-minus-pad">
							<div className="double-padding"></div>
							<span className="font-5 margin-bottom bold-font color-blue">TCPs</span>
							{selectedGripper !== null &&
								selectedGripper.tcps.map((pTcp: any, pIdx: number) => {
									if (pIdx === 0 && !slipSheetPresent) {
										return void 0;
									}
									return (
										<div
											className="flex-col full-width-minus-pad border rounded padding double-margin-bottom"
											key={pIdx}
										>
											<div className="flex-row full-width flex-between">
												<span className="font-6 margin-bottom bold-font color-blue">
													{pIdx === 0 ? 'Slip Sheet' : `Pick ${pIdx}`}
												</span>
												<Button
													pAction={() => deleteTcp(pIdx)}
													pDisabled={pIdx !== selectedGripper.tcps.length - 1 || pIdx < 2}
												>
													<span>Delete TCP</span>
												</Button>
											</div>

											<div className="padding"></div>
											<div className="padding"></div>
											<span className="font-5 margin-bottom bold-font color-blue">
												Offset
											</span>
											<div className="flex-row flex-between full-width">
												<div className="flex-col">
													<span className="margin-bottom bold-font color-blue">X (mm)</span>
													<WrappedInput
														field={'X offset'}
														value={pTcp?.offset?.X}
														unitsSystem={void 0}
														setter={pNewValue => {
															updateTcpOffset(Number(pNewValue), pTcp, pIdx, 'X');
														}}
														triggerKeyboard={lKeyboardTrigger}
													/>
												</div>
												<div className="flex-col">
													<span className="margin-bottom bold-font color-blue">Y (mm)</span>
													<WrappedInput
														field={'Y offset'}
														value={pTcp?.offset?.Y}
														unitsSystem={void 0}
														setter={pNewValue => {
															updateTcpOffset(Number(pNewValue), pTcp, pIdx, 'Y');
														}}
														triggerKeyboard={lKeyboardTrigger}
													/>
												</div>
												<div className="flex-col">
													<span className="margin-bottom bold-font color-blue">Z (mm)</span>
													<WrappedInput
														field={'Z offset'}
														value={pTcp?.offset?.Z}
														unitsSystem={void 0}
														setter={pNewValue => {
															updateTcpOffset(Number(pNewValue), pTcp, pIdx, 'Z');
														}}
														triggerKeyboard={lKeyboardTrigger}
													/>
												</div>
											</div>
											<div className="padding"></div>
											<div className="flex-row flex-between full-width">
												<div className="flex-col">
													<span className="margin-bottom bold-font color-blue">Rx</span>
													<WrappedInput
														field={'Rx'}
														value={pTcp?.offset?.Rx}
														unitsSystem={void 0}
														setter={pNewValue => {
															updateTcpOffset(Number(pNewValue), pTcp, pIdx, 'Rx');
														}}
														triggerKeyboard={lKeyboardTrigger}
													/>
												</div>
												<div className="flex-col">
													<span className="margin-bottom bold-font color-blue">Ry</span>
													<WrappedInput
														field={'Ry'}
														value={pTcp?.offset?.Ry}
														unitsSystem={void 0}
														setter={pNewValue => {
															updateTcpOffset(Number(pNewValue), pTcp, pIdx, 'Ry');
														}}
														triggerKeyboard={lKeyboardTrigger}
													/>
												</div>
												<div className="flex-col">
													<span className="margin-bottom bold-font color-blue">Rz</span>
													<WrappedInput
														field={'Rz'}
														value={pTcp?.offset?.Rz}
														unitsSystem={void 0}
														setter={pNewValue => {
															updateTcpOffset(Number(pNewValue), pTcp, pIdx, 'Rz');
														}}
														triggerKeyboard={lKeyboardTrigger}
													/>
												</div>
											</div>
											<div className="flex-col">
												<div className="double-padding"></div>
												<span className="font-5 margin-bottom bold-font color-blue">
													Sensors
												</span>
												{pTcp?.sensors &&
													pTcp.sensors.map(
														(pSensor: { [key: string]: any }, pSensorIdx: number) => {
															return (
																<div
																	key={pSensorIdx}
																	className="margin-bottom flex-between flex-row"
																>
																	<div className="full-height flex-row">
																		<ComboBox
																			values={Object.values(SENSOR_TYPE)}
																			currentValue={pSensor['sensorType']}
																			onChange={pNewValue => {
																				const lNewTcp = { ...pTcp };
																				lNewTcp.sensors[pSensorIdx].sensorType = pNewValue;
																				const lNew = { ...selectedGripper };
																				lNew.tcps[pIdx] = lNewTcp;
																				updateGripper(lNew);
																			}}
																		></ComboBox>
																		<div className="padding"></div>
																		<IORender
																			net={pSensor?.id}
																			pin={pSensor?.pin}
																			inverse_logic={pSensor['ReverseLogic']}
																			onChange={pNewValue => {
																				const lNew = { ...selectedGripper };
																				const lNewTcp = { ...pTcp };
																				lNewTcp.sensors[pSensorIdx] = {
																					id: pNewValue.net,
																					pin: pNewValue.pin,
																					ReverseLogic: pNewValue.ReverseLogic,
																					sensorType: pTcp.sensors[pSensorIdx].sensorType,
																				};

																				lNew.tcps[pIdx] = lNewTcp;
																				updateGripper(lNew);
																			}}
																		/>
																	</div>
																</div>
															);
														}
													)}
											</div>
											<div className="flex-col full-width">
												<div className="double-padding"></div>
												<span className="font-5 margin-bottom bold-font color-blue">
													Actuators
												</span>
												{!!pTcp?.actuators &&
													pTcp.actuators.map(
														(pActuator: { [key: string]: any }, pActuatorIdx: number) => {
															return (
																<div
																	key={pActuatorIdx}
																	className="flex-row flex-between full-width margin-bottom"
																>
																	<IORender
																		net={pActuator?.id}
																		pin={pActuator?.pin}
																		inverse_logic={pActuator?.ReverseLogic}
																		onChange={pNewValue => {
																			const lNew = { ...selectedGripper };
																			const lNewTcp = { ...pTcp };
																			lNewTcp.actuators[pActuatorIdx] = {
																				id: pNewValue.net,
																				pin: pNewValue.pin,
																				ReverseLogic: pNewValue.ReverseLogic,
																			};
																			lNew.tcps[pIdx] = lNewTcp;
																			updateGripper(lNew);
																		}}
																	/>
																	<div
																		className={
																			'cursor-pointer font-large flex-center hover-bold hover-underline padding-right' +
																			(pTcp.actuators.length === 1
																				? ' color-dark-grey'
																				: ' color-red')
																		}
																		onClick={() => {
																			if (pTcp.actuators.length === 1) return;
																			deleteActuator(pIdx, pActuatorIdx);
																		}}
																	>
																		<span className="large-font bold-font icon-delete"></span>
																	</div>
																</div>
															);
														}
													)}
												<div className="full-width flex-row double-margin-top">
													<Button className="vw-8" pAction={() => addActuator(pIdx)}>
														<span className="bold-font padding-left padding-right">
															Add Actuator
														</span>
													</Button>
												</div>
											</div>
										</div>
									);
								})}
							<div className="flex-row flex-end full-width">
								<Button className="vw-12" pAction={addTcp}>
									<span className="bold-font">Add TCP</span>
								</Button>
							</div>
						</div>
						<div className="double-padding"></div>
						<div className="double-padding"></div>
						<div className="double-padding"></div>
						<div className="double-padding"></div>
						<div className="double-padding"></div>
						<div className="double-padding"></div>
					</Scroll>
				)}
			</div>
		</div>
	);
};

export default Grippers;
